import React, { useState, useEffect } from 'react';
import {
    Button,
    Typography,
    Space,
    Card,
    Table,
    Tag,
    Dropdown,
    message,
    Popconfirm,
    Tooltip
} from 'antd';
import {
    PhoneOutlined,
    PlusOutlined,
    FileTextOutlined,
    CoffeeOutlined,
    StopOutlined,
    MoreOutlined,
    EditOutlined,
    DeleteOutlined,
    AudioOutlined,
    AudioMutedOutlined,
    UserAddOutlined,
    TeamOutlined
} from '@ant-design/icons';
import PBXFormsDrawer from '../components/PBXFormsDrawer';
import BreaksDrawer from '../components/BreaksDrawer';
import BlacklistDrawer from '../components/BlacklistDrawer';
import CampaignDrawer from '../components/CampaignDrawer';
import CampaignContactsDrawer from '../components/CampaignContactsDrawer';
import CampaignAgentsDrawer from '../components/CampaignAgentsDrawer';
import { useAuth } from '../context/AuthContext';
import { campaignService } from '../services/api';

const { Title } = Typography;

const CampaignsPage = () => {
    const auth = useAuth() || {};
    const permissions = Array.isArray(auth.permissions) ? auth.permissions : [];
    const canViewBreaks = permissions.includes('callcenter.campaigns.breaks.view');
    const canViewBlacklist = permissions.includes('callcenter.campaigns.blacklist.view');
    const canCreateCampaigns = permissions.includes('callcenter.campaigns.create');
    const canUpdateCampaigns = permissions.includes('callcenter.campaigns.update');
    const canDeleteCampaigns = permissions.includes('callcenter.campaigns.delete');
    const canAssignCampaigns = permissions.includes('callcenter.campaigns.assign');
    const canViewForms = permissions.includes('callcenter.campaigns.forms.view');

    const [loading, setLoading] = useState(false);
    const [campaigns, setCampaigns] = useState([]);

    // Drawers visibility
    const [formsDrawerVisible, setFormsDrawerVisible] = useState(false);
    const [breaksDrawerVisible, setBreaksDrawerVisible] = useState(false);
    const [blacklistDrawerVisible, setBlacklistDrawerVisible] = useState(false);
    const [campaignDrawerVisible, setCampaignDrawerVisible] = useState(false);
    const [contactsDrawerVisible, setContactsDrawerVisible] = useState(false);
    const [agentsDrawerVisible, setAgentsDrawerVisible] = useState(false);

    // Selected campaign for edit or assignment
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    useEffect(() => {
        // Set current navigation ID for audits (Hardcoded for Campaigns module: 7)
        localStorage.setItem('currentNavigationId', '7');
        fetchCampaigns();
    }, []);

    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const res = await campaignService.list();
            if (res.status === 'success') {
                setCampaigns(res.data);
            }
        } catch (error) {
            message.error('Error al cargar campañas');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedCampaign(null);
        setCampaignDrawerVisible(true);
    };

    const handleEdit = (record) => {
        setSelectedCampaign(record);
        setCampaignDrawerVisible(true);
    };

    const handleContacts = (record) => {
        setSelectedCampaign(record);
        setContactsDrawerVisible(true);
    };

    const handleManageAgents = (record) => {
        setSelectedCampaign(record);
        setAgentsDrawerVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await campaignService.delete(id);
            if (res.status === 'success') {
                message.success('Campaña eliminada correctamente');
                fetchCampaigns();
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al eliminar campaña';
            message.error(errorMsg);
        }
    };

    const columns = [
        {
            title: 'Prioridad',
            dataIndex: 'weight',
            key: 'weight',
            width: 100,
            align: 'center',
            render: (weight) => <Tag color="blue">{weight}</Tag>
        },
        {
            title: 'Nombre',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Typography.Text strong>{text}</Typography.Text>
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag color={type === 'inbound' ? 'cyan' : 'geekblue'}>
                    {type === 'inbound' ? 'ENTRANTE' : 'SALIENTE'}
                </Tag>
            )
        },
        {
            title: 'Formulario',
            dataIndex: 'form_name',
            key: 'form_name',
            render: (name) => name || <Typography.Text type="secondary">Ninguno</Typography.Text>
        },
        {
            title: 'Grabación',
            dataIndex: 'record',
            key: 'record',
            align: 'center',
            render: (record) => (
                record === 1 ?
                    <Tooltip title="Grabado activo"><AudioOutlined style={{ color: '#52c41a' }} /></Tooltip> :
                    <Tooltip title="Sin grabación"><AudioMutedOutlined style={{ color: '#bfbfbf' }} /></Tooltip>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (active) => (
                <Tag color={active === 1 ? 'success' : 'warning'}>
                    {active === 1 ? 'ACTIVA' : 'PAUSADA'}
                </Tag>
            )
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            render: (_, record) => {
                return (
                    <Space>
                        {canAssignCampaigns && (
                            <Tooltip title="Agentes">
                                <Button
                                    icon={<TeamOutlined />}
                                    onClick={() => handleManageAgents(record)}
                                    style={{ color: '#1890ff', borderColor: '#1890ff' }}
                                />
                            </Tooltip>
                        )}
                        {canAssignCampaigns && record.type === 'outbound' && (
                            <Tooltip title="Contactos">
                                <Button
                                    icon={<UserAddOutlined />}
                                    onClick={() => handleContacts(record)}
                                    style={{ color: '#52c41a', borderColor: '#52c41a' }}
                                />
                            </Tooltip>
                        )}
                        {canUpdateCampaigns && (
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => handleEdit(record)}
                            />
                        )}
                        {canDeleteCampaigns && (
                            <Popconfirm
                                title="¿Estás seguro de eliminar esta campaña?"
                                description="Esta acción no se puede deshacer si no hay dependencias."
                                onConfirm={() => handleDelete(record.id)}
                                okText="Sí, eliminar"
                                cancelText="No"
                                okButtonProps={{ danger: true }}
                            >
                                <Button icon={<DeleteOutlined />} danger />
                            </Popconfirm>
                        )}
                        {!canAssignCampaigns && !canUpdateCampaigns && !canDeleteCampaigns && (
                            <Typography.Text type="secondary">Solo lectura</Typography.Text>
                        )}
                    </Space>
                );
            }
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2} style={{ margin: 0 }}>
                        <PhoneOutlined /> Gestión de Campañas
                    </Title>
                </div>
                <Space>
                    {canViewBreaks && (
                        <Button
                            icon={<CoffeeOutlined />}
                            onClick={() => setBreaksDrawerVisible(true)}
                            size="large"
                        >
                            Gestionar Breaks
                        </Button>
                    )}
                    {canViewBlacklist && (
                        <Button
                            icon={<StopOutlined />}
                            onClick={() => setBlacklistDrawerVisible(true)}
                            size="large"
                        >
                            Blacklist
                        </Button>
                    )}
                    {canViewForms && (
                        <Button
                            icon={<FileTextOutlined />}
                            onClick={() => setFormsDrawerVisible(true)}
                            size="large"
                        >
                            Gestionar Formularios
                        </Button>
                    )}
                    {canCreateCampaigns && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="large"
                            onClick={handleCreate}
                        >
                            Nueva Campaña
                        </Button>
                    )}
                </Space>
            </div>

            <Card className="standard-card">
                <Table
                    dataSource={campaigns}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                />
            </Card>

            <PBXFormsDrawer
                visible={formsDrawerVisible}
                onClose={() => setFormsDrawerVisible(false)}
            />

            {canViewBreaks && (
                <BreaksDrawer
                    visible={breaksDrawerVisible}
                    onClose={() => setBreaksDrawerVisible(false)}
                />
            )}

            {canViewBlacklist && (
                <BlacklistDrawer
                    visible={blacklistDrawerVisible}
                    onClose={() => setBlacklistDrawerVisible(false)}
                />
            )}

            <CampaignDrawer
                visible={campaignDrawerVisible}
                onClose={() => setCampaignDrawerVisible(false)}
                campaign={selectedCampaign}
                onSuccess={fetchCampaigns}
            />

            <CampaignContactsDrawer
                visible={contactsDrawerVisible}
                onClose={() => setContactsDrawerVisible(false)}
                campaign={selectedCampaign}
                onSuccess={fetchCampaigns}
            />

            <CampaignAgentsDrawer
                visible={agentsDrawerVisible}
                onClose={() => setAgentsDrawerVisible(false)}
                campaign={selectedCampaign}
            />
        </div>
    );
};

export default CampaignsPage;
